import JsSIPConstants from 'jssip/lib/Constants';
import DialogClass from 'jssip/lib/Dialog';
import DialogRequestSender from 'jssip/lib/Dialog/RequestSender';
import JsSIP from 'jssip/lib/JsSIP';
import type { ReInviteEvent, RenegotiateOptions } from 'jssip/lib/RTCSession';
import { OutgoingRequest } from 'jssip/lib/SIPMessage';
import Transactions from 'jssip/lib/Transactions';
import sdpTransform from 'sdp-transform';

import { ICE_SERVERS } from '../config';
import type { TKindTrack } from '../model/types';
import { ensureCodec, remoteAcceptsByKind } from './media';

export const calculate491RetryDelay = (
	direction: 'incoming' | 'outgoing' | null | undefined
): number => {
	const isOriginator = direction === 'outgoing';
	const min = isOriginator ? 2100 : 0; // ms
	const max = isOriginator ? 4000 : 2000; // ms
	const step = 10;
	const steps = Math.floor((max - min) / step);
	return min + Math.floor(Math.random() * (steps + 1)) * step;
};

const NOTIFY_PATCH_FLAG = '__app_notify_guard__';
const SUPPORTED_PATCH_FLAG = '__app_supported_patch__';
const SUPPORTED_HEADER_RE = /^\s*supported\s*:\s*(.+)$/i;
const REFER_TO_HEADER_RE = /^\s*refer-to\s*:\s*(.+)$/i;

/** Символ‑маркер, помечающий NOTIFY как уже обработанный, чтобы не отвечать повторно. */
export const NOTIFY_HANDLED_FLAG = Symbol('jssipAdapter.notifyHandled');
const prepareIncomingRequest = (ua: any, request: any): boolean => {
	const method = request?.method;
	if (!method) return false;

	const uriUser = request?.ruri?.user;
	const uaUser = ua?._configuration?.uri?.user;
	const contactUser = ua?._contact?.uri?.user;
	if (uriUser !== uaUser && uriUser !== contactUser) {
		if (method !== JsSIPConstants.ACK) {
			request?.reply_sl?.(404);
		}
		return false;
	}

	if (request?.ruri?.scheme === JsSIPConstants.SIPS) {
		request?.reply_sl?.(416);
		return false;
	}

	if (Transactions.checkTransaction(ua, request)) {
		return false;
	}

	if (method === JsSIPConstants.INVITE) {
		// eslint-disable-next-line no-new
		new Transactions.InviteServerTransaction(ua, ua._transport, request);
	} else if (method !== JsSIPConstants.ACK && method !== JsSIPConstants.CANCEL) {
		// eslint-disable-next-line no-new
		new Transactions.NonInviteServerTransaction(ua, ua._transport, request);
	}

	return true;
};

/** true → требуется создать новый RTCPeerConnection */
const needsNewPeerConnection = (prevRemoteSdp: string, incomingOffer: string): boolean => {
	if (!prevRemoteSdp) return false; // первый INVITE

	const old = sdpTransform.parse(prevRemoteSdp);
	const neu = sdpTransform.parse(incomingOffer);

	if (old.media.length !== neu.media.length) return true;

	for (let i = 0; i < old.media.length; i += 1) {
		const a = old.media[i];
		const b = neu.media[i];

		// тип
		if (a.type !== b.type) return true;

		/* 2. ICE-restart (ufrag/pwd поменялись на уровне всей сессии) */
		if (a.iceUfrag !== b.iceUfrag || a.icePwd !== b.icePwd) return true;

		// протокол (UDP/TLS/RTP/SAVPF vs RTP/SAVPF …)
		if (a.protocol !== b.protocol) return true;

		// mid для BUNDLE
		if (a.mid !== b.mid) return true;

		// набор payload-type’ов
		const pA = (a.payloads || '').split(' ').filter(Boolean).sort().join(' ');
		const pB = (b.payloads || '').split(' ').filter(Boolean).sort().join(' ');
		if (pA !== pB) return true;
	}
	return false;
};

const waitIceComplete = async (pc: RTCPeerConnection) => {
	if (pc.iceGatheringState === 'complete') return;
	if (
		pc.getTransceivers().length === 0 ||
		pc.getTransceivers().some((tx) => tx.direction === 'stopped')
	)
		return;

	await new Promise<void>((res) => {
		const h = () => {
			if (pc.iceGatheringState === 'complete') {
				pc.removeEventListener('icegatheringstatechange', h);
				res();
			}
		};
		pc.addEventListener('icegatheringstatechange', h);
	});
};

const makeLocalSdp = async (
	pc: RTCPeerConnection,
	type: 'offer' | 'answer'
): Promise<RTCSessionDescriptionInit> => {
	const desc =
		type === 'offer' ? await pc.createOffer({ iceRestart: true }) : await pc.createAnswer();

	await pc.setLocalDescription(desc); // (1) запускаем ICE-gathering
	await waitIceComplete(pc); // (2) ждём кандидатов

	return pc.localDescription!; // (3) «полный» SDP
};

const createReplacementPc = (pcOld: RTCPeerConnection | undefined): RTCPeerConnection => {
	const pcNew = new RTCPeerConnection({
		iceServers: ICE_SERVERS,
		rtcpMuxPolicy: 'require',
		// bundlePolicy: 'max-bundle',
		// iceCandidatePoolSize: 2
	});

	/* подписки */
	pcNew.onicecandidate = pcOld?.onicecandidate || null;
	pcNew.ontrack = pcOld?.ontrack || null;
	pcNew.onconnectionstatechange = pcOld?.onconnectionstatechange || null;
	return pcNew;
};

const patchRequestSender = () => {
	const originalReceiveResponse = DialogRequestSender.prototype._receiveResponse;
	DialogRequestSender.prototype._receiveResponse = async function (response: any) {
		try {
			const isInvite =
				response?.method === JsSIPConstants.INVITE ||
				this._request?.method === JsSIPConstants.INVITE;
			if (isInvite && response.status_code === 491) {
				const session = this._dialog?.owner;

				await rollbackLocalOffer(session.connection as RTCPeerConnection);

				if (session.isEnded?.()) return;

				const retryAfterMs = calculate491RetryDelay(session.direction);

				session.emit('reinviteRequestPending', {
					retryAfterMs,
					response,
				});
				return;
			}
		} catch (error) {
			console.warn('[ReINVITE 491] custom handler failed', error);
		}

		return originalReceiveResponse.call(this, response);
	};

	const originalCheck = DialogClass.prototype._checkInDialogRequest;
	DialogClass.prototype._checkInDialogRequest = function (request: any) {
		const isInviteLike =
			request?.method === JsSIPConstants.INVITE ||
			(request?.method === JsSIPConstants.UPDATE && Boolean(request?.body));

		if (isInviteLike && (this._uac_pending_reply === true || this._uas_pending_reply === true)) {
			try {
				const info = this._owner.__appInfo;
				const dirMap = remoteAcceptsByKind(request.body);
				info.pendingRemoteDir = dirMap;
				request.reply(491);
			} catch (error) {
				console.warn('[491] failed to reply pending INVITE', error);
			}
			return false;
		}

		return originalCheck.call(this, request);
	};
};

/**
 * Патчит прототип JsSIP.UA: добавляет методы subscribe/notify и охрану NOTIFY.
 *
 * Идемпотентен — повторный вызов не добавит дубликаты.
 */
export const patchMethod = () => {
	patchRequestSender();
	// Чтоб не добавить дубликат — лучше чуть безопаснее:
	if (!JsSIPConstants.ALLOWED_METHODS.includes('SUBSCRIBE')) {
		(JsSIPConstants as any).ALLOWED_METHODS += ',SUBSCRIBE';
	}

	const proto = JsSIP.UA.prototype as any;

	// TODO убрать
	if (!proto[NOTIFY_PATCH_FLAG]) {
		const originalReceiveRequest = proto.receiveRequest;
		proto.receiveRequest = function (request: any) {
			try {
				if (request?.method === JsSIPConstants.NOTIFY && request.to_tag) {
					const dialog = this._findDialog?.(request.call_id, request.from_tag, request.to_tag);
					const session = dialog ? null : this._findSession?.(request);
					if (!dialog && !session) {
						if (!prepareIncomingRequest(this, request)) {
							return;
						}
						this.emit('notify', { request, orphan: true });
						if (!request?.[NOTIFY_HANDLED_FLAG]) {
							request.reply(481, 'Subscription does not exist');
						}
						return;
					}
				}
			} catch (error) {
				console.warn('[JsSIP patch] notify guard failed', error);
			}
			return originalReceiveRequest.call(this, request);
		};
		proto[NOTIFY_PATCH_FLAG] = true;
	}

	// Объединяем/дедуплицируем дополнительные Supported и Refer-To из extraHeaders,
	// чтобы не дублировать заголовки (нужно для norefersub и кастомного Refer-To на REFER).
	const outgoingProto = OutgoingRequest.prototype as any;
	if (!outgoingProto[SUPPORTED_PATCH_FLAG]) {
		const originalToString = outgoingProto.toString;

		outgoingProto.toString = function () {
			const supportedTokens: string[] = [];
			const referToValues: string[] = [];

			// 1) Собираем Supported/Refer-To из extraHeaders и убираем их,
			// чтобы не выводились отдельно.
			if (Array.isArray(this.extraHeaders)) {
				this.extraHeaders = this.extraHeaders.filter((header: string) => {
					const supportedMatch = SUPPORTED_HEADER_RE.exec(header);
					if (supportedMatch) {
						supportedTokens.push(
							...supportedMatch[1]
								.split(',')
								.map((t) => t.trim())
								.filter(Boolean)
						);
						return false;
					}

					const referToMatch = REFER_TO_HEADER_RE.exec(header);
					if (referToMatch) {
						referToValues.push(referToMatch[1].trim());
						return false;
					}

					return true;
				});
			}

			// 2) Если кто-то вызвал setHeader('Supported'/'Refer-To', ...), тоже мёржим и очищаем.
			if (this.headers) {
				for (const key of Object.keys(this.headers)) {
					const values = this.headers[key] || [];
					const lowerKey = key.toLowerCase();

					if (lowerKey === 'supported') {
						values.forEach((val: string) => {
							supportedTokens.push(
								...String(val)
									.split(',')
									.map((t) => t.trim())
									.filter(Boolean)
							);
						});
						delete this.headers[key]; // иначе оригинальный toString выведет второй Supported
					}

					if (lowerKey === 'refer-to') {
						values.forEach((val: string) => {
							const asString = String(val).trim();
							if (asString) referToValues.push(asString);
						});
						delete this.headers[key]; // иначе оригинальный toString выведет второй Refer-To
					}
				}
			}

			let msg = originalToString.call(this) as string;
			if (supportedTokens.length > 0) {
				const supportedLine = msg.match(/supported:\s*([^\n\r]*)/i);
				if (supportedLine) {
					const merged = [
						...new Set([
							...supportedLine[1]
								.split(',')
								.map((t) => t.trim())
								.filter(Boolean),
							...supportedTokens,
						]),
					];
					msg = msg.replace(/supported:\s*[^\n\r]*/i, `Supported: ${merged.join(', ')}`);
				}
			}

			if (referToValues.length > 0) {
				const referToHeader = `Refer-To: ${referToValues[0]}`;
				const referToLineRe = /\r\nrefer-to\s*:[^\n\r]*/i;

				if (referToLineRe.test(msg)) {
					msg = msg.replace(referToLineRe, `\r\n${referToHeader}`);
				} else if (msg.includes('\r\n\r\n')) {
					msg = msg.replace('\r\n\r\n', `\r\n${referToHeader}\r\n\r\n`);
				} else {
					msg += `\r\n${referToHeader}`;
				}
			}

			return msg;
		};

		outgoingProto[SUPPORTED_PATCH_FLAG] = true;
	}
};

/**
 * Патчит конкретную RTCSession: корректно обрабатывает входящие re-INVITE/UPDATE,
 * пересоздаёт PeerConnection при необходимости и переносит существующие треки.
 *
 * @param info Объект SessionInfo с session и текущими флагами.
 */
export const patchSession = (info: any) => {
	const { session } = info;
	session.__appInfo = info;
	/* ---------- входящий Re-INVITE ---------- */
	const origProcessOffer = session._processInDialogSdpOffer?.bind(session);
	if (typeof origProcessOffer === 'function') {
		session._processInDialogSdpOffer = async function (req: any) {
			const offerSdp = req.body;
			const pcOld = this._connection as RTCPeerConnection | undefined;

			if (pcOld?.getTransceivers().every((tx) => tx.direction === 'stopped'))
				return origProcessOffer(req);

			if (needsNewPeerConnection(pcOld?.remoteDescription?.sdp || '', offerSdp)) {
				const remoteDir = remoteAcceptsByKind(offerSdp);
				const pcNew = createReplacementPc(pcOld);
				this._connection = pcNew;

				const kindCounter: Record<string, number> = {};
				await pcNew.setRemoteDescription({ type: 'offer', sdp: offerSdp });

				if (pcOld && !info.isOnHold.remote) {
					for (const txOld of pcOld.getTransceivers()) {
						const oldTrack = txOld.sender.track;
						const oldKind = oldTrack?.kind;

						if (!oldKind) continue;

						const remoteDirection = remoteDir[oldKind as TKindTrack];

						// Удалённый участник убрал m-линию (например, video) — не переносим её,
						// чтобы последующие offer’ы не добавляли «лишний» блок обратно.
						if (!remoteDirection) {
							txOld.stop?.();
							continue;
						}

						kindCounter[oldKind] = (kindCounter[oldKind] || 0) + 1;
						if (kindCounter[oldKind] > 1) continue;

						let txNew = pcNew.getTransceivers().find((tx) => tx.mid?.includes(oldKind));

						if (txNew) {
							// eslint-disable-next-line no-await-in-loop -- последовательная замена треков безопаснее
							await txNew.sender.replaceTrack(oldTrack); // ⬅️ ждём завершения
						} else {
							const sender = pcNew.addTrack(oldTrack);
							txNew = pcNew.getTransceivers().find((t) => t.sender === sender)!;
						}

						if (txOld.currentDirection !== 'inactive') {
							txNew.direction =
								txOld.currentDirection === 'sendonly' &&
								txOld.direction === 'sendonly' &&
								remoteDirection === 'sendrecv'
									? 'sendrecv'
									: txOld.direction;
						}
					}
				}
				ensureCodec(pcNew);

				const answer = await makeLocalSdp(pcNew, 'answer'); // ← ОДИН раз

				this._last_sdp = answer.sdp;
				this._rtcAnswer = answer.sdp;
				session.emit('peerconnection', { peerconnection: pcNew });
				pcOld?.close();
			}
			return origProcessOffer(req); // штатная логика JsSIP
		};
	}

	/* ---------- блокируем обработку re-INVITE, если уже есть исходящий/входящий pending ---------- */
	const origReceiveReinvite = session._receiveReinvite?.bind(session);
	if (typeof origReceiveReinvite === 'function') {
		session._receiveReinvite = function (req: any) {
			try {
				// Dialog._checkInDialogRequest уже отправил 491/500, не трогаем PC пока транзакция не завершится.
				if (this._dialog?.uac_pending_reply === true) {
					return;
				}
			} catch (error) {
				console.warn('[ReINVITE guard] failed to check pending state', error);
			}
			return origReceiveReinvite(req);
		};
	}

	/* ---------- исходящий Re-INVITE / renogotiate() ---------- */
	const origRenegotiate = session.renegotiate?.bind(session);
	if (typeof origRenegotiate === 'function') {
		session.renegotiate = async function (opts: RenegotiateOptions, done?: () => void) {
			const pcOld = this._connection as RTCPeerConnection;

			if (pcOld.getTransceivers().every((tx) => tx.direction === 'stopped'))
				return origRenegotiate(opts);

			const preview = await pcOld.createOffer({ iceRestart: true });

			if (needsNewPeerConnection(pcOld.remoteDescription?.sdp || '', preview.sdp || '')) {
				const pcNew = createReplacementPc(pcOld);

				const kindCounter: Record<string, number> = {};
				pcOld.getTransceivers().forEach((txOld) => {
					const oldTrack = txOld.sender.track;
					const kind =
						oldTrack?.kind ||
						txOld.receiver?.track?.kind ||
						(txOld.mid && /video/i.test(txOld.mid) ? 'video' : undefined);

					if (kind) {
						kindCounter[kind] = (kindCounter[kind] || 0) + 1;
						// Оставляем по одному transceiver на каждый kind
						if (kindCounter[kind] > 1) return;
					}

					if (!kind) return;

					if (oldTrack) {
						pcNew.addTransceiver(oldTrack, { direction: txOld.direction });
					} else {
						pcNew.addTransceiver(kind, { direction: txOld.direction });
					}
				});

				ensureCodec(pcNew);
				this._connection = pcNew;
				this._remoteHold = false;

				const offer = await makeLocalSdp(pcNew, 'offer'); // ← ОДИН раз

				this._last_sdp = offer.sdp;
				this._rtcOffer = offer.sdp;
				session.emit('peerconnection', { peerconnection: pcNew });
				pcOld.close();
			}
			return origRenegotiate(opts, done); // дальше всё как обычно
		};
	}

	/* ---------- внутренний createLocalDescription ---------- */
	const originCreateLocalDescription = session._createLocalDescription?.bind(session);
	if (typeof originCreateLocalDescription === 'function') {
		session._createLocalDescription = async function (type: string, rtcConstraints = {}) {
			/* 1. Делаем setLocalDescription() как обычно — но БЕЗ ожидания ICE */
			const pc: RTCPeerConnection = this._connection;

			ensureCodec(pc);
			/* 2. Если есть активные транспорта — отдаём управление оригиналу,
                  чтобы он дождался null-candidate или complete. */
			if (!info.isOnHold.remote && !info.isOnHold.local) {
				return originCreateLocalDescription(type, rtcConstraints);
			}

			// Шаг 1 оригинала: build SDP
			const desc =
				type === 'offer'
					? await pc.createOffer(rtcConstraints)
					: await pc.createAnswer(rtcConstraints);

			await pc.setLocalDescription(desc);

			if (!info.isOnHold.remote && !this._iceReady) {
				this._iceReady = true;
			}

			const ld = this._connection.localDescription!;
			const ev = { originator: 'local', type, sdp: ld.sdp };
			this.emit('sdp', ev); // хуки могут изменить ev.sdp
			this._last_sdp = ev.sdp;

			return this._last_sdp; // ← ВСЕГДА отдаём строку SDP
		};
	}

	/* ---------- ACK guard (входящие сессии receiveRequest): игнорируем ACK с неверным CSeq ---------- */
	const originalReceiveRequest = session.receiveRequest.bind(session);
	if (typeof originalReceiveRequest === 'function') {
		try {
			// работаем только на UAS (входящий вызов)
			if (info.session.direction === 'incoming') {
				// 1) ожидаемый CSeq для ACK на первоначальный INVITE
				session._expectedAckCSeq = session._request?.cseq ?? null;
				// 2) при каждом re-INVITE запоминаем новый ожидаемый CSeq
				info.session.on('reinvite', (evt: ReInviteEvent) => {
					session._expectedAckCSeq = (evt.request as any)?.cseq ?? session._expectedAckCSeq;
				});

				session.receiveRequest = function (request: any) {
					try {
						if (
							request?.method === JsSIP.C.ACK &&
							this._status === session.C.STATUS_WAITING_FOR_ACK
						) {
							// ожидаемый CSeq: сначала explicit, иначе из исходного INVITE
							const expected: number | null = this._expectedAckCSeq ?? this._request?.cseq ?? null;

							if (typeof expected === 'number' && request.cseq !== expected) {
								// КРИТИЧНО: не трогаем статус/таймеры — пусть 200 OK ретрансмитится
								console.warn(
									`[ACK-guard] Ignore ACK with wrong CSeq=${request.cseq}, expected=${expected}`
								);
								return; // глотаем невалидный ACK
							}
						}
					} catch (error) {
						console.error('[ACK-guard] error:', error);
						// в случае ошибки отдаём управление оригинальной логике
					}
					return originalReceiveRequest(request);
				};
			}
		} catch (error) {
			console.error('[ACK-guard] init failed', error);
		}
	}
};

export const rollbackLocalOffer = async (pc?: RTCPeerConnection | null): Promise<void> => {
	if (!pc) return;
	if (pc.signalingState !== 'have-local-offer') return;

	try {
		await pc.setLocalDescription({ type: 'rollback' } as any);
	} catch (error) {
		console.warn('[491] rollback failed', error);
	}
};
